<script>
import OrgChartNode4 from './OrgChartNode4.vue';
import SpellCheckedText from "@/components/SpellCheckedText.vue";

export default {
    name: "OrgChartTemp5",
    components: {
        OrgChartNode4,
        SpellCheckedText
    },
    props: {
        orgChart: { type: Object, required: true },
        startPage: { type: Number, default: 1 },
    },
    data() {
        return {
            NODE_WIDTH: 210,
            NODE_HEIGHT: 70,
            LEVEL_GAP: 30,
            AVAILABLE_WIDTH: 920,
            AVAILABLE_HEIGHT: 480
        };
    },
    computed: {
        treeData() {
            return this.buildTree(this.orgChart.members);
        },
        treeScale() {
            const requiredWidth = this.getTreeWidth(this.treeData);
            const maxDepth = this.getMaxDepth(this.treeData);
            const requiredHeight = maxDepth * this.NODE_HEIGHT + (maxDepth - 1) * this.LEVEL_GAP;

            const widthScale = requiredWidth > this.AVAILABLE_WIDTH ? Math.max(0.3, this.AVAILABLE_WIDTH / requiredWidth) : 1;
            const heightScale = requiredHeight > this.AVAILABLE_HEIGHT ? Math.max(0.3, this.AVAILABLE_HEIGHT / requiredHeight) : 1;

            return Math.min(widthScale, heightScale);
        }
    },
    methods: {
        buildTree(members) {
            const map = {};
            const roots = [];

            if (!members || members.length === 0) return [];

            const membersCopy = members.map(member => ({
                ...member,
                sourceMember: member,
                children: []
            }));

            membersCopy.forEach(member => {
                map[member.teamMemberId] = member;
            });

            membersCopy.forEach(member => {
                if (member.reportsToId && map[member.reportsToId]) {
                    map[member.reportsToId].children.push(member);
                } else {
                    roots.push(member);
                }
            });

            return roots;
        },
        getMaxDepth(nodes) {
            if (!nodes || nodes.length === 0) return 0;
            let maxDepth = 1;
            nodes.forEach(node => {
                if (node.children && node.children.length > 0) {
                    maxDepth = Math.max(maxDepth, 1 + this.getMaxDepth(node.children));
                }
            });
            return maxDepth;
        },
        getTreeWidth(nodes) {
            if (!nodes || nodes.length === 0) return 0;
            let totalWidth = 0;
            nodes.forEach(node => {
                if (!node.children || node.children.length === 0) {
                    totalWidth += this.NODE_WIDTH;
                } else {
                    const childrenWidth = this.getTreeWidth(node.children);
                    totalWidth += Math.max(this.NODE_WIDTH, childrenWidth);
                }
            });
            return totalWidth;
        }
    }
};
</script>

<template>
    <div class="main-container page-unit" :style="{
        '--header-color': orgChart.style.colorScheme && orgChart.style.colorScheme.header,
        '--body-color': orgChart.style.colorScheme && orgChart.style.colorScheme.body,
        '--footer-color': orgChart.style.colorScheme && orgChart.style.colorScheme.footer,
        '--heading-color': orgChart.style.heading.color,
        '--heading-size': orgChart.style.heading.size + 'px',
        '--heading-weight': orgChart.style.heading.bold ? 'bold' : 'normal',
        '--heading-style': orgChart.style.heading.italic ? 'italic' : 'normal',
        '--heading-decoration': orgChart.style.heading.underline ? 'underline' : 'none',
        '--heading-font': orgChart.style.heading.font,
        '--label-color': orgChart.style.label.color,
        '--label-size': orgChart.style.label.size + 'px',
        '--label-weight': orgChart.style.label.bold ? 'bold' : 'normal',
        '--label-weight-bold': orgChart.style.label.bold ? '800' : '600',
        '--label-style': orgChart.style.label.italic ? 'italic' : 'normal',
        '--label-decoration': orgChart.style.label.underline ? 'underline' : 'none',
        '--label-font': orgChart.style.label.font,
        '--text-color': orgChart.style.text.color,
        '--text-size': orgChart.style.text.size + 'px',
        '--text-weight': orgChart.style.text.bold ? 'bold' : 'normal',
        '--text-style': orgChart.style.text.italic ? 'italic' : 'normal',
        '--text-decoration': orgChart.style.text.underline ? 'underline' : 'none',
        '--text-font': orgChart.style.text.font,
    }">

        <div class="chart-container">
            <div class="chart-area">
                <h1 class="page-title">
                    <SpellCheckedText :value="orgChart.title || ''" @input="orgChart.about.title = $event" />
                </h1>
                <div class="line"></div>

                <div class="tree-container">
                    <ul class="tree" :style="{ transform: `scale(${treeScale})`, transformOrigin: 'top center' }">
                        <OrgChartNode4 v-for="node in treeData" :key="node.teamMemberId" :node="node" />
                    </ul>
                </div>
            </div>
        </div>

        <div class="about-section">
            <h4 class="about-title">
                <SpellCheckedText :value="orgChart.about.title || ''" @input="orgChart.about.title = $event" />
            </h4>
            <p class="description">
                <SpellCheckedText :value="orgChart.about && orgChart.about.description || ''"
                    @input="orgChart.about.description = $event" />
            </p>
        </div>

        <div class="footer-bottom">
            <div class="footer-content">
                <div class="footer-left">
                    <p class="footer-text">
                        <SpellCheckedText :value="orgChart.opprName || ''" @input="orgChart.opprName = $event" />
                    </p>
                </div>
                <div class="footer-right">
                    <p class="footer-company">
                        <SpellCheckedText :value="orgChart.submittedBy || ''" @input="orgChart.submittedBy = $event" />
                    </p>
                    <!-- <div class="footer-divider">|</div> -->
                    <div class="page-badge">{{ String(startPage + pageIndex).padStart(2, '0') }}</div>			
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped lang="scss">
.main-container {
    display: flex;
    flex-direction: column;
    width: 297mm;
    height: 210mm;
    position: relative;
    box-sizing: border-box;
    background: white;
    padding: 30px;

    &:hover {
        ::v-deep .node-card .photobox .photo-overlay {
            opacity: 1;
        }

        ::v-deep .node-card .photobox img {
            opacity: 0.9;
        }
    }

    .chart-container {
        display: flex;
        flex-direction: column;
        justify-content: flex-start;
        align-items: center;
        flex: 1;
        height: 559.71px;
        overflow: hidden;

        .chart-area {
            display: flex;
            flex-direction: column;
            justify-content: flex-start;
            align-items: center;
            max-height: 559.71px;
            padding-top: 10px;

            .page-title {
                color: var(--heading-color);
                font-size: var(--heading-size);
                font-family: var(--heading-font);
                font-weight: var(--heading-weight);
                font-style: var(--heading-style);
                text-decoration: var(--heading-decoration);
                margin-bottom: 5px;
                text-transform: uppercase;
            }

            .line {
                width: 30px;
                height: 1px;
                background: #555;
                transform: rotate(90deg);
                margin: 10px 0;
            }

            .tree-container1 {
                flex: 1;
                display: flex;
                justify-content: center;
                align-items: flex-start;
                overflow: hidden;
                padding: 4px 20px 0;
            }

            .tree1 {
                position: relative;
                display: flex;
                flex-direction: column;
                align-items: center;
                padding-left: 0;
                margin: 0;

                ::v-deep ul {
                    padding-top: 10px;
                    position: relative;
                    display: flex;
                    justify-content: center;
                    padding-left: 0;
                    margin: 0;

                    &::before {
                        content: '';
                        position: absolute;
                        top: 0;
                        left: 50%;
                        border-left: 1.5px solid #555;
                        width: 0;
                        height: 10px;
                    }
                }

                ::v-deep li {
                    text-align: center;
                    list-style-type: none;
                    position: relative;
                    padding: 10px 8px 0 8px;

                    &::before,
                    &::after {
                        content: '';
                        position: absolute;
                        top: 0;
                        right: 50%;
                        border-top: 1.5px solid #555;
                        width: 50%;
                        height: 30px;
                    }

                    &::after {
                        right: auto;
                        left: 50%;
                        border-left: 1.5px solid #555;
                    }

                    &:only-child::after,
                    &:only-child::before {
                        display: none;
                    }

                    &:only-child {
                        padding-top: 0;
                    }

                    &:first-child::before,
                    &:last-child::after {
                        border: 0 none;
                    }

                    &:last-child::before {
                        border-right: 1.5px solid #555;
                        border-radius: 0 5px 0 0;
                    }

                    &:first-child::after {
                        border-radius: 5px 0 0 0;
                    }
                }
            }
        }
    }

    .about-section {
        height: 200px;
        width: 40%;
        margin: 20px 50px;
        padding: 10px;
        border-radius: 10px;
        background: var(--header-color);
        overflow: hidden;

        .about-title {
            color: var(--heading-color);
            font-size: var(--heading-size);
            font-family: var(--heading-font);
            font-weight: var(--heading-weight);
            font-style: var(--heading-style);
            text-decoration: var(--heading-decoration);
            text-transform: uppercase;
            margin-bottom: 5px;
        }

        .description {
            color: var(--text-color);
            font-size: var(--text-size);
            font-family: var(--text-font);
            font-weight: var(--text-weight);
            font-style: var(--text-style);
            text-decoration: var(--text-decoration);
            margin: 0;
            white-space: pre-wrap;
        }
    }

    .footer-bottom {
        padding: 0 30px;

        .footer-content {
            display: flex;
            justify-content: space-between;
            align-items: center;

            .footer-left {
                .footer-text {
                    margin: 0;
                    font-size: 12px;
                    color: var(--footer-color);
                    font-family: Helvetica;
                    font-weight: bold;
                }
            }

            .footer-right {
                display: flex;
                align-items: center;
                justify-content: flex-end;
                text-align: right;

                .footer-company {
                    font-weight: bold;
                    font-size: 12px;
                    margin: 0;
                    font-family: Helvetica;
                    color: var(--footer-color);
                }

                .footer-divider {
                    font-size: 12px;
                    font-weight: bold;
                    margin: 0;
                    padding: 0px 10px;
                }

                .page-badge {
                    font-size: 16px;
                    color: var(--heading-color);
                    font-weight: 800;
                    margin: 0;
                }
            }
        }
    }
}
.tree-container {
    width: 100%;
    display: flex;
    justify-content: center;
}

// The root ul — uses same CSS connector pattern as Temp1/Temp2
.tree {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding-left: 0;
    margin: 0;

    // All nested ul (children lists)
    ::v-deep ul {
        padding-top: 10px;
        position: relative;
        display: flex;
        justify-content: center;
        padding-left: 0;
        margin: 0;

        // Vertical line down from parent to the horizontal bar
        &::before {
            content: '';
            position: absolute;
            top: 0;
            left: 50%;
            border-left: 1.5px solid #555;
            width: 0;
            height: 10px;
        }
    }

    // All li items (including nested)
    ::v-deep li {
        text-align: center;
        list-style-type: none;
        position: relative;
        padding: 10px 8px 0 8px;

        // Horizontal connecting line segments
        &::before,
        &::after {
            content: '';
            position: absolute;
            top: 0;
            right: 50%;
            border-top: 1.5px solid #555;
            width: 50%;
            height: 30px;
        }

        &::after {
            right: auto;
            left: 50%;
            border-left: 1.5px solid #555;
        }

        // Single child — hide horizontal lines, remove top padding
        &:only-child::after,
        &:only-child::before {
            display: none;
        }

        &:only-child {
            padding-top: 0;
        }

        // First and last child edge cleanup
        &:first-child::before,
        &:last-child::after {
            border: 0 none;
        }

        &:last-child::before {
            border-right: 1.5px solid #555;
            border-radius: 0 5px 0 0;
        }

        &:first-child::after {
            border-radius: 5px 0 0 0;
        }
    }
}

</style>
